import { auth } from "@portal/lib/firebase/client";
import axios from "@portal/lib/axios";
import type { User } from "firebase/auth";
import { createStore } from "zustand/vanilla";

const localStorageKeyGreaterOpsViewEnabled = "greaterOpsViewEnabled";

/**
 * This store is used to store the user's information and the authentication state.
 * This should only store auth information pulled from Firebase, _not_ the Greater User identity.
 *
 * To fetch the Greater User, @see user-query.ts
 */

export type UserState = {
  uid: string;
  name: string;
  email?: string;
  displayName?: string;
  tenantId?: string;
  isGreaterOps: boolean;
  isGreaterOpsViewEnabled: boolean;
  isLoadingAuth: boolean;
  firebaseUser: User | null;
};

export type UserActions = {
  reset: () => void;
  setFirebaseUser: (firebaseUser: User | null) => void;
  setIsGreaterOps: (isGreaterOps: UserState["isGreaterOps"]) => void;
  setIsGreaterOpsViewEnabled: (isGreaterOpsViewEnabled: boolean) => void;
  setIsLoadingAuth: (isLoadingAuth: UserState["isLoadingAuth"]) => void;
};

export type UserStore = UserState & UserActions;

export const defaultUserState: UserState = {
  uid: "",
  name: "",
  firebaseUser: null,
  isGreaterOps: false,
  isGreaterOpsViewEnabled: true,
  isLoadingAuth: true,
  email: "",
  displayName: "",
  tenantId: "",
};

export const initUserStore = (): UserState => {
  return {
    ...defaultUserState,
    isGreaterOpsViewEnabled: getStoredGreaterOpsViewEnabled(),
  };
};

export const createUserStore = (initState: UserState = defaultUserState) => {
  return createStore<UserStore>()((set) => ({
    ...initState,
    firebaseUser: initState.firebaseUser ?? auth.currentUser,
    reset: () => set(initUserStore()),
    setFirebaseUser: (firebaseUser) => {
      setTenantIdHeaderInRequestContext(firebaseUser?.tenantId ?? "");
      set({
        firebaseUser,
        name: firebaseUser?.displayName ?? "",
        email: firebaseUser?.email ?? "",
        displayName: firebaseUser?.displayName ?? "",
        tenantId: firebaseUser?.tenantId ?? "",
        uid: firebaseUser?.uid ?? "",
      });
    },
    setIsGreaterOps: (isGreaterOps) => set({ isGreaterOps }),
    setIsGreaterOpsViewEnabled: (isGreaterOpsViewEnabled) => {
      setStoredGreaterOpsViewEnabled(isGreaterOpsViewEnabled);
      set({ isGreaterOpsViewEnabled });
    },
    setIsLoadingAuth: (isLoadingAuth) => set({ isLoadingAuth }),
  }));
};

function setTenantIdHeaderInRequestContext(tenantId: string) {
  axios.defaults.headers.common["Grpc-Metadata-X-Tenant-ID"] = tenantId;
}

function getStoredGreaterOpsViewEnabled() {
  if (typeof window === "undefined") {
    return true;
  }

  try {
    const value = localStorage.getItem(localStorageKeyGreaterOpsViewEnabled);
    return value === null ? true : value === "true";
  } catch {
    return true;
  }
}

function setStoredGreaterOpsViewEnabled(isEnabled: boolean) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(
      localStorageKeyGreaterOpsViewEnabled,
      String(isEnabled),
    );
  } catch {
    // Ignore localStorage write failures and keep the in-memory preference.
  }
}
